import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useHistory } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronLeft, faChevronRight, faTrashCanXmark } from "@fortawesome/pro-regular-svg-icons";

import { StatusCode } from "api/protocols";
import {
  GetReservationModuleAll,
  GetReservationTemplate,
  GetReservationTemplateCalendar,
  IReservationCalendarDay,
  PutApplyReservationTemplate,
} from "api/rpc/2024-04/facilityAdmin/reservation/reservation";
import { useAppDispatch } from "hooks/redux";
import useModal from "hooks/modals/useModal";
import { showError, showSuccess } from "redux/actions/ui";
import { IModule, ITemplate } from "redux/reducers/models/reservations";

import Page from "components/page/Page";
import Callout from "components/callout/Callout";
import Sheet from "components/sheet/Sheet";
import SelectNew from "components/select/SelectNew";
import DatePickerInput from "components/datePickerInput/DatePickerInput";
import FormLayout from "components/form/FormLayout";
import Checkbox from "components/form/checkbox/Checkbox";
import FilterDropdown from "components/filterDropDown/FilterDropDown";
import DataTable from "components/dataTable/DataTable";
import { ButtonNew as Button } from "components/buttonNew";
import { Select } from "components/select/index";
import { useWindowSize } from "hooks/useWindowSize/useWindowSize";
import { TABLET_WIDTH } from "helpers/ScreenSizes";
import Portal from "elements/Portal";
import ReservationCalendarDay from "./ReservationCalendarDay";

import "../../teesheets/calendar/teesheetCalendar.scss";
import "./reservationCalendarDay.scss";

interface ISelectedDate {
  date: string;
  template_id: number | null;
  day_of_week: string;
}

const dayOfWeekOptions = [
  { label: "Mondays", value: "monday" },
  { label: "Tuesdays", value: "tuesday" },
  { label: "Wednesdays", value: "wednesday" },
  { label: "Thursdays", value: "thursday" },
  { label: "Fridays", value: "friday" },
  { label: "Saturdays", value: "saturday" },
  { label: "Sundays", value: "sunday" },
];

function daysBeforeMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth(), 1).getDay();
}

function daysInMonth(date: Date) {
  return new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();
}

export default function ReservationCalendar() {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useAppDispatch();
  const windowSize = useWindowSize();
  const { Option } = Select;
  const today = new Date();

  const [modules, setModules] = useState<IModule[]>(undefined);
  const [moduleId, setModuleId] = useState<number>(null);
  const [templates, setTemplates] = useState<ITemplate[]>([]);
  const [days, setDays] = useState<IReservationCalendarDay[]>([]);
  const [calendarDate, setCalendarDate] = useState(new Date(today.getFullYear(), today.getMonth(), 1));
  const [setupDate, setSetupDate] = useState<string | null>(null);

  const {
    state: applyRangeModal,
    closeModal: closeApplyRangeModal,
    updateModal: updateApplyRangeModal,
  } = useModal({
    clearOnClose: true,
    date_range_start: new Date(),
    date_range_end: new Date(),
    default_template_id: null as number,
    monday_template_id: null as number,
    tuesday_template_id: null as number,
    wednesday_template_id: null as number,
    thursday_template_id: null as number,
    friday_template_id: null as number,
    saturday_template_id: null as number,
    sunday_template_id: null as number,
    days_of_week: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
    selected_dates: [] as ISelectedDate[],
    step: 1,
    apply_to_all_days: false,
    apply_to_specific_days: false,
  });

  const dayLookup = useMemo(() => {
    const lookup: Record<string, IReservationCalendarDay> = {};
    days.forEach(day => {
      lookup[day.date] = day;
    });
    return lookup;
  }, [days]);

  useEffect(() => {
    void loadModules();
  }, []);

  useEffect(() => {
    if (!moduleId) {
      return;
    }
    void loadTemplates(moduleId);
    void loadCalendar(moduleId, calendarDate);
  }, [moduleId, calendarDate]);

  async function loadModules() {
    const res = await GetReservationModuleAll({}, true);
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error getting activity types")));
      setModules([]);
      return;
    }
    const loaded = res.data ?? [];
    setModules(loaded);
    if (loaded.length > 0) {
      setModuleId(loaded[0].id);
    }
  }

  async function loadTemplates(nextModuleId: number) {
    const res = await GetReservationTemplate({ module_id: nextModuleId }, false);
    const loadedTemplates: ITemplate[] =
      res.status === StatusCode.OK && Array.isArray(res.data) ? (res.data as ITemplate[]) : [];
    setTemplates(loadedTemplates);
  }

  async function loadCalendar(nextModuleId: number, date: Date) {
    const res = await GetReservationTemplateCalendar(
      {
        module_id: nextModuleId,
        year: date.getFullYear(),
        month: date.getMonth() + 1,
      },
      false,
    );
    if (res.status !== StatusCode.OK) {
      dispatch(showError(t("Error loading calendar")));
      setDays([]);
      return;
    }
    setDays(res.data?.days ?? []);
  }

  function shiftMonth(amount: number) {
    setCalendarDate(prev => new Date(prev.getFullYear(), prev.getMonth() + amount, 1));
  }

  function openDay(date: string, day: IReservationCalendarDay | undefined, isCurrentOrFuture: boolean) {
    if (!moduleId) {
      return;
    }
    if (day?.template_name || day?.scheduled) {
      history.push(`/admin/reservation/edit/${moduleId}?date=${date}&from=calendar`);
      return;
    }
    if (isCurrentOrFuture) {
      setSetupDate(date);
    }
  }

  function handleAddDates() {
    const dates = applyRangeModal.selected_dates.slice();
    const startDate = new Date(applyRangeModal.date_range_start);
    const endDate = new Date(applyRangeModal.date_range_end);

    for (let d = startDate; d <= endDate; d.setDate(d.getDate() + 1)) {
      const dateKey = moment(d).format("YYYY-MM-DD");
      const dateExists = dates.find(date => date.date === dateKey);
      const dayName = moment(d).format("dddd").toLocaleLowerCase();
      if (!dateExists && applyRangeModal.days_of_week.find(day => day === dayName)) {
        dates.push({
          date: dateKey,
          template_id: null,
          day_of_week: moment(d).format("dddd"),
        });
      }
    }

    dates.sort((date1, date2) => date1.date.localeCompare(date2.date));
    updateApplyRangeModal({ selected_dates: dates });
  }

  function applyRangeOkDisabled() {
    if (applyRangeModal.step === 1) {
      return applyRangeModal.selected_dates.length <= 0;
    }
    if (applyRangeModal.step === 2) {
      if (!applyRangeModal.apply_to_all_days && !applyRangeModal.apply_to_specific_days) {
        return true;
      }
      if (applyRangeModal.apply_to_all_days && applyRangeModal.default_template_id === null) {
        return true;
      }
      if (applyRangeModal.apply_to_specific_days) {
        return dayOfWeekOptions.some(day => {
          const included = applyRangeModal.selected_dates.some(
            date => date.day_of_week.toLocaleLowerCase() === day.value,
          );
          return included && applyRangeModal[`${day.value}_template_id`] === null;
        });
      }
    }
    return false;
  }

  function continueFromTemplateStep() {
    const updatedSelectedDates = applyRangeModal.selected_dates.map(selectedDate => {
      const templateProperty = `${selectedDate.day_of_week.toLocaleLowerCase()}_template_id`;
      const templateId = applyRangeModal.apply_to_specific_days
        ? applyRangeModal[templateProperty]
        : applyRangeModal.default_template_id;
      return { ...selectedDate, template_id: templateId };
    });
    updateApplyRangeModal({ selected_dates: updatedSelectedDates, step: 3 });
  }

  async function handleApplyRange() {
    const templateData = applyRangeModal.selected_dates
      .filter(date => date.template_id)
      .map(date => ({ date: date.date, template_id: date.template_id }));

    if (templateData.length === 0) {
      dispatch(showError(t("No dates selected")));
      return;
    }

    const res = await PutApplyReservationTemplate({ template_data: templateData }, true);
    if (res?.status !== StatusCode.OK) {
      dispatch(showError(typeof res?.data === "string" ? res.data : t("Error applying templates")));
      return;
    }

    closeApplyRangeModal();
    dispatch(showSuccess(t("Templates added to queue successfully")));
    void loadCalendar(moduleId, calendarDate);
  }

  function applyRangeTitle() {
    switch (applyRangeModal.step) {
      case 1:
        return t("Select Range");
      case 2:
        return t("Select Templates");
      case 3:
        return t("Review");
      default:
        return t("Apply Templates to Range");
    }
  }

  const monthOptions = moment.months().map((month, index) => ({ id: index + 1, label: month }));
  const thisYear = new Date().getFullYear();
  const yearOptions = [thisYear - 1, thisYear, thisYear + 1, thisYear + 2];
  const paddingDays = daysBeforeMonth(calendarDate);
  const monthLength = daysInMonth(calendarDate);
  const trailingDays = 7 * 6 - (paddingDays + monthLength);

  return (
    <Page
      title={t("Calendar")}
      subtitle={t("View templates applied throughout the season")}
      primaryAction={{
        content: t("Apply Templates to Range"),
        action: () => updateApplyRangeModal({ isOpen: true }),
        disabled: !moduleId,
      }}
    >
      {modules?.length === 0 ? (
        <Callout
          type="info"
          title={t("No Activity Types Created")}
          content={t("Create an activity type before applying templates.")}
        />
      ) : (
        <>
          {modules && modules.length > 1 ? (
            <div className="courses-dropdown-container">
              <SelectNew
                id="activity-type"
                label={t("Activity Type")}
                value={moduleId}
                onChange={value => {
                  setModuleId(value);
                }}
                options={modules.map(module => ({ id: module.id, label: module.title }))}
              />
            </div>
          ) : null}

          <div className="calendar-quick-date-selection">
            <button className="navigation-button" type="button" onClick={() => shiftMonth(-1)}>
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
            <div className="calendar-quick-date-picker">
              <SelectNew
                id="calendar-month"
                value={calendarDate.getMonth() + 1}
                onChange={month => {
                  setCalendarDate(new Date(calendarDate.getFullYear(), Number(month) - 1, 1));
                }}
                options={monthOptions}
              />
              <SelectNew
                id="calendar-year"
                value={calendarDate.getFullYear()}
                onChange={year => {
                  setCalendarDate(new Date(Number(year), calendarDate.getMonth(), 1));
                }}
                options={yearOptions.map(year => ({ id: year, label: year }))}
              />
            </div>
            <button className="navigation-button" type="button" onClick={() => shiftMonth(1)}>
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>

          <div className="grid-container">
            {moment.weekdays().map(day => (
              <div key={day} className="day-title">
                <p>{day.substring(0, 3)}</p>
              </div>
            ))}
            {Array.from(Array(paddingDays).keys()).map(index => (
              <div key={`pad-${index}`} className="grid-item" />
            ))}
            {Array.from(Array(monthLength).keys()).map(index => {
              const date = moment(new Date(calendarDate.getFullYear(), calendarDate.getMonth(), index + 1)).format(
                "YYYY-MM-DD",
              );
              const day = dayLookup[date];
              const isCurrentOrFuture = moment(date).isSameOrAfter(moment().format("YYYY-MM-DD"), "day");
              const hasSchedule = Boolean(day?.template_name || day?.scheduled);
              const clickable = hasSchedule || isCurrentOrFuture;
              return (
                <div
                  key={date}
                  className={`grid-item teesheet unselected-teesheet${clickable ? "" : " reservation-calendar-day-static"}`}
                  onClick={() => openDay(date, day, isCurrentOrFuture)}
                >
                  <div className="day-of-month">
                    <p>{index + 1}</p>
                  </div>
                  <div className="grid-content">
                    {day?.template_name ? (
                      <div className="template-title-wrap">
                        <span className="template-title">{day.template_name}</span>
                        {day.template_version ? (
                          <span className="calendar-version">
                            {t("Version")} {day.template_version}
                          </span>
                        ) : null}
                      </div>
                    ) : day?.scheduled ? (
                      <span className="template-title">{t("Scheduled")}</span>
                    ) : null}
                  </div>
                </div>
              );
            })}
            {Array.from(Array(Math.max(trailingDays, 0)).keys()).map(index => (
              <div key={`trail-${index}`} className="grid-item" />
            ))}
          </div>
        </>
      )}

      {setupDate && moduleId ? (
        <ReservationCalendarDay
          key={setupDate}
          open
          date={setupDate}
          templates={templates}
          onClose={() => setSetupDate(null)}
          onApplied={() => {
            setSetupDate(null);
            void loadCalendar(moduleId, calendarDate);
          }}
          onGenerated={() => history.push(`/admin/reservation/edit/${moduleId}?date=${setupDate}&from=calendar`)}
        />
      ) : null}

      <Portal isMounted={applyRangeModal.isOpen}>
        <Sheet
          open={applyRangeModal.isOpen}
          title={applyRangeTitle()}
          size="medium"
          onCancel={() => {
            if (applyRangeModal.step === 1) {
              closeApplyRangeModal();
              return;
            }
            updateApplyRangeModal({ step: applyRangeModal.step - 1 });
          }}
          onOk={() => {
            if (applyRangeModal.step === 1) {
              updateApplyRangeModal({ step: 2 });
              return;
            }
            if (applyRangeModal.step === 2) {
              continueFromTemplateStep();
              return;
            }
            void handleApplyRange();
          }}
          okText={applyRangeModal.step !== 3 ? t("Continue") : t("Apply")}
          cancelText={applyRangeModal.step !== 1 ? t("Back") : t("Cancel")}
          okDisabled={applyRangeOkDisabled()}
          closable
        >
          {applyRangeModal.step === 1 ? (
            <FormLayout>
              <FormLayout.Group>
                <DatePickerInput
                  label={t("Range")}
                  months={windowSize.width > TABLET_WIDTH ? 2 : 1}
                  position="left"
                  startingDate={applyRangeModal.date_range_start}
                  setStartingDate={(date: Date) => updateApplyRangeModal({ date_range_start: date })}
                  endingDate={applyRangeModal.date_range_end}
                  setEndingDate={(date: Date) => updateApplyRangeModal({ date_range_end: date })}
                  disablePastDays
                />
                <div className="flex gap-2">
                  <FilterDropdown
                    label={t("Days to Add")}
                    data={dayOfWeekOptions}
                    dataLabelProperty="label"
                    checkBoxId="days_of_week"
                    dataValueProperty="value"
                    onCheckBoxChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                      const { value, checked } = event.target;
                      const selected = [...applyRangeModal.days_of_week];
                      const index = selected.indexOf(value);
                      if (checked && index === -1) {
                        selected.push(value);
                      }
                      if (!checked && index !== -1) {
                        selected.splice(index, 1);
                      }
                      updateApplyRangeModal({ days_of_week: selected });
                    }}
                    onClear={() => updateApplyRangeModal({ days_of_week: [] })}
                    onAddAll={() =>
                      updateApplyRangeModal({
                        days_of_week: ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"],
                      })
                    }
                    defaultCheckBoxes={dayOfWeekOptions.map(day => applyRangeModal.days_of_week.includes(day.value))}
                  />
                  <Button type="primary" onClick={handleAddDates}>
                    {t("Add")}
                  </Button>
                </div>
              </FormLayout.Group>
              <FormLayout.Group>
                <DataTable
                  overflow
                  columns={[
                    { label: t("Date") },
                    { label: t("Day of Week") },
                    {
                      label: "",
                      width: "5%",
                      content: (
                        <Button
                          onClick={() => updateApplyRangeModal({ selected_dates: [] })}
                          className="text-gray-600"
                          type="text"
                        >
                          {t("Clear")}
                        </Button>
                      ),
                    },
                  ]}
                >
                  {applyRangeModal.selected_dates.map((date, index) => (
                    <tr key={date.date}>
                      <td>{moment(date.date).format("ll")}</td>
                      <td>{date.day_of_week}</td>
                      <td className="text-center">
                        <FontAwesomeIcon
                          onClick={() => {
                            const next = applyRangeModal.selected_dates.slice();
                            next.splice(index, 1);
                            updateApplyRangeModal({ selected_dates: next });
                          }}
                          icon={faTrashCanXmark}
                          className="cursor-pointer"
                        />
                      </td>
                    </tr>
                  ))}
                </DataTable>
              </FormLayout.Group>
            </FormLayout>
          ) : null}
          {applyRangeModal.step === 2 ? (
            <FormLayout>
              <FormLayout.Group>
                <Checkbox
                  label={t("Apply One Template to All Selected Dates")}
                  checked={applyRangeModal.apply_to_all_days}
                  onChange={event =>
                    updateApplyRangeModal({
                      apply_to_all_days: event.target.checked,
                      apply_to_specific_days: event.target.checked ? false : applyRangeModal.apply_to_specific_days,
                    })
                  }
                  size="medium"
                />
              </FormLayout.Group>
              <FormLayout.Group>
                <Checkbox
                  label={t("Apply Different Templates to Specific Days of the Week")}
                  checked={applyRangeModal.apply_to_specific_days}
                  onChange={event =>
                    updateApplyRangeModal({
                      apply_to_specific_days: event.target.checked,
                      apply_to_all_days: event.target.checked ? false : applyRangeModal.apply_to_all_days,
                    })
                  }
                  size="medium"
                />
              </FormLayout.Group>
              {applyRangeModal.apply_to_all_days ? (
                <FormLayout.Group>
                  <Select
                    label={t("Template")}
                    onChange={(value: number) => updateApplyRangeModal({ default_template_id: value })}
                  >
                    {templates.map(template => (
                      <Option value={template.id} key={template.id}>
                        {template.name}
                      </Option>
                    ))}
                  </Select>
                </FormLayout.Group>
              ) : null}
              {applyRangeModal.apply_to_specific_days
                ? dayOfWeekOptions
                    .filter(day =>
                      applyRangeModal.selected_dates.some(
                        date => date.day_of_week.toLocaleLowerCase() === day.value,
                      ),
                    )
                    .map(day => (
                      <FormLayout.Group key={day.value}>
                        <Select
                          label={t("{{day}} Template", { day: t(day.label) })}
                          onChange={(value: number) => updateApplyRangeModal({ [`${day.value}_template_id`]: value })}
                        >
                          {templates.map(template => (
                            <Option value={template.id} key={template.id}>
                              {template.name}
                            </Option>
                          ))}
                        </Select>
                      </FormLayout.Group>
                    ))
                : null}
            </FormLayout>
          ) : null}
          {applyRangeModal.step === 3 ? (
            <DataTable
              columns={[
                { label: t("Date") },
                { label: t("Day of Week") },
                { label: t("Template") },
              ]}
            >
              {applyRangeModal.selected_dates.map(date => (
                <tr key={date.date}>
                  <td>{moment(date.date).format("ll")}</td>
                  <td>{date.day_of_week}</td>
                  <td>{templates.find(template => template.id === date.template_id)?.name}</td>
                </tr>
              ))}
            </DataTable>
          ) : null}
        </Sheet>
      </Portal>
    </Page>
  );
}
